"use client";

import { useState, useEffect, useMemo } from "react";

interface TrendFilterResult {
  symbol: string;
  price: number;
  ma: number;
  below_ma: boolean;
  distance_pct: number;
  change_pct: number;
  volume: number;
  date?: string;
}

interface TrendTabProps {
  timeframe: "daily" | "weekly" | "monthly";
}

export default function TrendTab({ timeframe }: TrendTabProps) {
  const [selectedMA, setSelectedMA] = useState<20 | 50 | 100 | 200>(20);
  const [strictMode, setStrictMode] = useState(false);
  const [results, setResults] = useState<TrendFilterResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [symbolQuery, setSymbolQuery] = useState("");

  // Fetch data when timeframe or filters change
  useEffect(() => {
    const fetchTrendData = async () => {
      try {
        setLoading(true);
        setError(null);

        const endpoint = strictMode
          ? "/api/trend/below-all-mas"
          : "/api/trend/below-ma";

        const response = await fetch(endpoint, {
          method: strictMode ? "GET" : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          ...(strictMode
            ? { /* GET query params handled below */ }
            : {
                body: JSON.stringify({
                  ma_period: selectedMA,
                  timeframe,
                }),
              }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch trend data");
        }

        const data = await response.json();

        if (strictMode) {
          // For strict mode, we get results_by_period
          if (data.results_by_period) {
            const allResults = Object.values(data.results_by_period).flat() as TrendFilterResult[];
            setResults(allResults);
          }
        } else {
          // For single MA, we get results array
          setResults(data.results || []);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTrendData();
  }, [selectedMA, timeframe, strictMode]);

  // Filter results by symbol query
  const filteredResults = useMemo(() => {
    const query = symbolQuery.trim().toUpperCase();
    return results.filter(
      (item) => !query || item.symbol.toUpperCase().includes(query)
    );
  }, [results, symbolQuery]);

  // Get status text based on distance
  const getStatusText = (distance: number) => {
    const absDist = Math.abs(distance);
    if (absDist > 10) return "Significantly Below";
    if (absDist > 5) return "Moderately Below";
    if (absDist > 2) return "Slightly Below";
    return "Just Below";
  };

  // Get color based on distance
  const getStatusColor = (distance: number) => {
    const absDist = Math.abs(distance);
    if (absDist > 10) return "#ef4444"; // Red
    if (absDist > 5) return "#f97316"; // Orange
    if (absDist > 2) return "#eab308"; // Yellow
    return "#84cc16"; // Lime
  };

  return (
    <div style={{ marginBottom: "2rem" }}>
      {/* Filters Section */}
      <div
        style={{
          background: "rgba(15, 23, 42, 0.6)",
          border: "1px solid rgba(148, 163, 184, 0.25)",
          borderRadius: "0.75rem",
          padding: "1.5rem",
          marginBottom: "2rem",
        }}
      >
        <h3
          style={{
            color: "var(--color-text)",
            fontSize: "1.1rem",
            fontWeight: "600",
            marginBottom: "1rem",
            margin: 0,
          }}
        >
          Trend Filter Controls
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "1.5rem",
            marginTop: "1rem",
          }}
        >
          {/* Moving Average Period */}
          <div>
            <label
              style={{
                display: "block",
                color: "rgba(241, 245, 249, 0.7)",
                fontSize: "0.85rem",
                marginBottom: "0.5rem",
              }}
            >
              Moving Average Period
            </label>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              {[20, 50, 100, 200].map((period) => (
                <button
                  key={period}
                  onClick={() => {
                    setSelectedMA(period as 20 | 50 | 100 | 200);
                    setStrictMode(false); // Reset strict mode when changing MA
                  }}
                  disabled={strictMode}
                  style={{
                    padding: "0.5rem 1rem",
                    background:
                      selectedMA === period && !strictMode
                        ? "var(--blue-text)"
                        : "rgba(15, 23, 42, 0.8)",
                    color: "var(--color-text)",
                    border:
                      selectedMA === period && !strictMode
                        ? "2px solid var(--blue-text)"
                        : "1px solid rgba(148, 163, 184, 0.25)",
                    borderRadius: "0.5rem",
                    cursor: strictMode ? "not-allowed" : "pointer",
                    fontSize: "0.9rem",
                    opacity: strictMode ? 0.5 : 1,
                    fontWeight: selectedMA === period && !strictMode ? "600" : "500",
                    transition: "all 0.2s ease",
                  }}
                >
                  {period}-day
                </button>
              ))}
            </div>
          </div>

          {/* Strict Mode Toggle */}
          <div>
            <label
              style={{
                display: "block",
                color: "rgba(241, 245, 249, 0.7)",
                fontSize: "0.85rem",
                marginBottom: "0.5rem",
              }}
            >
              Filter Mode
            </label>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <button
                onClick={() => setStrictMode(false)}
                style={{
                  padding: "0.5rem 1rem",
                  background: !strictMode ? "var(--blue-text)" : "rgba(15, 23, 42, 0.8)",
                  color: "var(--color-text)",
                  border: !strictMode
                    ? "2px solid var(--blue-text)"
                    : "1px solid rgba(148, 163, 184, 0.25)",
                  borderRadius: "0.5rem",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: !strictMode ? "600" : "500",
                  transition: "all 0.2s ease",
                }}
              >
                Single MA
              </button>
              <button
                onClick={() => setStrictMode(true)}
                style={{
                  padding: "0.5rem 1rem",
                  background: strictMode ? "var(--blue-text)" : "rgba(15, 23, 42, 0.8)",
                  color: "var(--color-text)",
                  border: strictMode
                    ? "2px solid var(--blue-text)"
                    : "1px solid rgba(148, 163, 184, 0.25)",
                  borderRadius: "0.5rem",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: strictMode ? "600" : "500",
                  transition: "all 0.2s ease",
                }}
              >
                Below All MAs
              </button>
            </div>
            <p
              style={{
                color: "rgba(241, 245, 249, 0.6)",
                fontSize: "0.8rem",
                marginTop: "0.5rem",
                margin: "0.5rem 0 0 0",
              }}
            >
              {strictMode
                ? "Shows stocks below all MAs (20, 50, 100, 200)"
                : `Shows stocks below ${selectedMA}-day MA`}
            </p>
          </div>

          {/* Symbol Search */}
          <div>
            <label
              style={{
                display: "block",
                color: "rgba(241, 245, 249, 0.7)",
                fontSize: "0.85rem",
                marginBottom: "0.5rem",
              }}
            >
              Search Symbol
            </label>
            <input
              type="text"
              value={symbolQuery}
              onChange={(e) => setSymbolQuery(e.target.value)}
              placeholder="e.g., HUBC, LUCK"
              style={{
                width: "100%",
                padding: "0.5rem 0.75rem",
                background: "rgba(15, 23, 42, 0.6)",
                color: "var(--color-text)",
                border: "1px solid rgba(148, 163, 184, 0.25)",
                borderRadius: "0.5rem",
                fontSize: "0.9rem",
              }}
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div
          style={{
            padding: "3rem 2rem",
            textAlign: "center",
            background: "var(--color-bg-secondary)",
            borderRadius: "var(--radius)",
          }}
        >
          <div
            style={{
              display: "inline-block",
              width: "40px",
              height: "40px",
              border: "4px solid rgba(59, 130, 246, 0.2)",
              borderTop: "4px solid var(--blue-text)",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <p style={{ color: "var(--color-text-muted)", marginTop: "1rem" }}>
            Scanning stocks...
          </p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div
          style={{
            padding: "2rem",
            background: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.5)",
            borderRadius: "var(--radius)",
            color: "#fca5a5",
          }}
        >
          <p>{error}</p>
        </div>
      )}

      {/* Results */}
      {!loading && !error && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1.5rem",
            }}
          >
            <h3 style={{ color: "var(--color-text)", margin: 0 }}>
              Results: {filteredResults.length} stocks
            </h3>
            <p
              style={{
                color: "rgba(241, 245, 249, 0.6)",
                fontSize: "0.9rem",
                margin: 0,
              }}
            >
              Timeframe: <strong>{timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}</strong>
            </p>
          </div>

          {filteredResults.length === 0 ? (
            <div
              style={{
                padding: "2rem",
                background: "var(--color-bg-secondary)",
                borderRadius: "var(--radius)",
                textAlign: "center",
                color: "rgba(241, 245, 249, 0.6)",
              }}
            >
              <p>No stocks found matching your criteria</p>
            </div>
          ) : (
            <div
              style={{
                overflowX: "auto",
                borderRadius: "var(--radius)",
                border: "1px solid rgba(148, 163, 184, 0.25)",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.9rem",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "rgba(15, 23, 42, 0.8)",
                      borderBottom: "1px solid rgba(148, 163, 184, 0.25)",
                    }}
                  >
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "left",
                        color: "rgba(241, 245, 249, 0.9)",
                        fontWeight: "600",
                      }}
                    >
                      Symbol
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "right",
                        color: "rgba(241, 245, 249, 0.9)",
                        fontWeight: "600",
                      }}
                    >
                      Price
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "right",
                        color: "rgba(241, 245, 249, 0.9)",
                        fontWeight: "600",
                      }}
                    >
                      MA {strictMode ? "(20)" : selectedMA}
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "right",
                        color: "rgba(241, 245, 249, 0.9)",
                        fontWeight: "600",
                      }}
                    >
                      Distance
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "center",
                        color: "rgba(241, 245, 249, 0.9)",
                        fontWeight: "600",
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "right",
                        color: "rgba(241, 245, 249, 0.9)",
                        fontWeight: "600",
                      }}
                    >
                      Change
                    </th>
                    <th
                      style={{
                        padding: "1rem",
                        textAlign: "right",
                        color: "rgba(241, 245, 249, 0.9)",
                        fontWeight: "600",
                      }}
                    >
                      Volume
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((result, idx) => (
                    <tr
                      key={`${result.symbol}-${idx}`}
                      style={{
                        borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
                        background: idx % 2 === 0 ? "rgba(15, 23, 42, 0.3)" : "transparent",
                        transition: "background 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(59, 130, 246, 0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          idx % 2 === 0 ? "rgba(15, 23, 42, 0.3)" : "transparent";
                      }}
                    >
                      <td
                        style={{
                          padding: "1rem",
                          color: "var(--color-text)",
                          fontWeight: "600",
                        }}
                      >
                        {result.symbol}
                      </td>
                      <td style={{ padding: "1rem", textAlign: "right", color: "var(--color-text)" }}>
                        {result.price.toFixed(2)}
                      </td>
                      <td style={{ padding: "1rem", textAlign: "right", color: "rgba(241, 245, 249, 0.8)" }}>
                        {result.ma.toFixed(2)}
                      </td>
                      <td
                        style={{
                          padding: "1rem",
                          textAlign: "right",
                          color:
                            result.distance_pct < -5
                              ? "#ef4444"
                              : result.distance_pct < -2
                              ? "#f97316"
                              : "#eab308",
                          fontWeight: "600",
                        }}
                      >
                        {result.distance_pct.toFixed(2)}%
                      </td>
                      <td
                        style={{
                          padding: "1rem",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "inline-block",
                            padding: "0.25rem 0.75rem",
                            background: `${getStatusColor(result.distance_pct)}20`,
                            color: getStatusColor(result.distance_pct),
                            borderRadius: "0.25rem",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                          }}
                        >
                          {getStatusText(result.distance_pct)}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "1rem",
                          textAlign: "right",
                          color:
                            result.change_pct > 0
                              ? "#22c55e"
                              : result.change_pct < 0
                              ? "#ef4444"
                              : "rgba(241, 245, 249, 0.8)",
                        }}
                      >
                        {result.change_pct > 0 ? "+" : ""}
                        {result.change_pct.toFixed(2)}%
                      </td>
                      <td style={{ padding: "1rem", textAlign: "right", color: "rgba(241, 245, 249, 0.8)" }}>
                        {(result.volume / 1000000).toFixed(1)}M
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}
